import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, ArrowRight, Hash, Zap, RefreshCw, Download, Bell, Activity, Users, ChevronRight, Command } from 'lucide-react';

const ACTIONS = [
  { id: 'nav-overview',  label: 'Go to Overview',       category: 'Navigate',  icon: Activity,   shortcut: 'G H',  tab: 'overview' },
  { id: 'nav-alerts',    label: 'Go to Alerts',          category: 'Navigate',  icon: Bell,       shortcut: 'G A',  tab: 'alerts' },
  { id: 'nav-meta',      label: 'Go to Meta Performance',category: 'Navigate',  icon: Hash,       shortcut: 'G M',  tab: 'meta' },
  { id: 'nav-google',    label: 'Go to Google Performance',category:'Navigate', icon: Hash,       shortcut: 'G G',  tab: 'google' },
  { id: 'nav-health',    label: 'Go to Health Overview', category: 'Navigate',  icon: Activity,   shortcut: '',     tab: 'health' },
  { id: 'nav-team',      label: 'Go to Team',            category: 'Navigate',  icon: Users,      shortcut: '',     tab: 'team' },
  { id: 'nav-recharge',  label: 'Go to Recharge',        category: 'Navigate',  icon: Zap,        shortcut: '',     tab: 'recharge' },
  { id: 'action-sync',   label: 'Trigger Sync Now',      category: 'Actions',   icon: RefreshCw,  shortcut: 'R',    action: 'sync' },
  { id: 'action-export', label: 'Export Accounts CSV',   category: 'Actions',   icon: Download,   shortcut: '',     action: 'export' },
  { id: 'action-alerts', label: 'Generate AI Alerts',    category: 'Actions',   icon: Bell,       shortcut: '',     action: 'generate-alerts' },
];

const CategoryIcon = ({ category }) => {
  const icons = {
    Navigate: <ArrowRight className="w-3 h-3" />,
    Actions:  <Zap className="w-3 h-3" />,
    Accounts: <Hash className="w-3 h-3" />,
  };
  return icons[category] || <Hash className="w-3 h-3" />;
};

const CommandPalette = ({ isOpen, onClose, accounts = [], onNavigate, onAction }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Build account items from accounts prop
  const accountItems = useMemo(() => accounts.slice(0, 50).map(acc => ({
    id: `account-${acc.id}`,
    label: acc.name,
    subtitle: `${acc.platform} · ${acc.campaign_count ?? ''} campaigns`,
    category: 'Accounts',
    icon: Hash,
    accountId: acc.id,
    platform: acc.platform,
  })), [accounts]);

  // Filter items based on query
  const allItems = useMemo(() => {
    const q = query.toLowerCase().trim();
    const filteredActions = q
      ? ACTIONS.filter(a => a.label.toLowerCase().includes(q) || a.category.toLowerCase().includes(q))
      : ACTIONS;
    const filteredAccounts = q
      ? accountItems.filter(a => a.label.toLowerCase().includes(q) || (a.subtitle || '').toLowerCase().includes(q))
      : accountItems.slice(0, 5);
    return [...filteredActions, ...filteredAccounts];
  }, [query, accountItems]);

  // Reset selection when query or open state changes
  useEffect(() => {
    setSelected(0);
  }, [query, isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelected(0);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selected}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const executeItem = useCallback((item) => {
    if (!item) return;
    if (item.tab) {
      onNavigate?.(item.tab);
    } else if (item.action) {
      onAction?.(item.action);
    } else if (item.accountId) {
      onNavigate?.('meta', item.accountId);
    }
    onClose();
  }, [onNavigate, onAction, onClose]);

  // Keyboard handler
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected(s => Math.min(s + 1, allItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected(s => Math.max(s - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        executeItem(allItems[selected]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, allItems, selected, executeItem, onClose]);

  if (!isOpen) return null;

  // Group items by category for display
  const groups = {};
  allItems.forEach((item, idx) => {
    if (!groups[item.category]) groups[item.category] = [];
    groups[item.category].push({ ...item, _idx: idx });
  });

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Palette */}
      <div
        className="relative w-full max-w-xl mx-4 rounded-2xl overflow-hidden shadow-2xl shadow-black/80 border border-white/10"
        style={{ background: 'rgba(9,9,11,0.97)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search input row */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10">
          <Search className="w-5 h-5 text-zinc-500 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search accounts, navigate, or run actions…"
            className="flex-1 bg-transparent text-white placeholder-zinc-600 text-sm outline-none font-medium"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-[10px] text-zinc-500 font-mono">
            ESC
          </kbd>
        </div>

        {/* Results list */}
        <div ref={listRef} className="max-h-[380px] overflow-y-auto overscroll-contain">
          {allItems.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-zinc-600 text-sm">No results for <span className="text-zinc-400">"{query}"</span></p>
            </div>
          ) : (
            Object.entries(groups).map(([category, items]) => (
              <div key={category}>
                {/* Category header */}
                <div className="flex items-center gap-2 px-4 py-2 sticky top-0 z-10" style={{ background: 'rgba(9,9,11,0.97)' }}>
                  <span className="text-zinc-600"><CategoryIcon category={category} /></span>
                  <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">{category}</span>
                </div>

                {/* Items */}
                {items.map((item) => {
                  const Icon = item.icon || Hash;
                  const isSelected = item._idx === selected;
                  return (
                    <button
                      key={item.id}
                      data-idx={item._idx}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => setSelected(item._idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isSelected ? 'bg-white/10' : 'hover:bg-white/5'
                      }`}
                    >
                      {/* Icon */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-white/5 text-zinc-500'
                      }`}>
                        {category === 'Accounts' && item.platform === 'META' ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        ) : category === 'Accounts' && item.platform === 'GOOGLE' ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4"/>
                            <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853"/>
                            <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05"/>
                            <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335"/>
                          </svg>
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                      </div>

                      {/* Label + subtitle */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                          {item.label}
                        </p>
                        {item.subtitle && (
                          <p className="text-xs text-zinc-600 truncate mt-0.5">{item.subtitle}</p>
                        )}
                      </div>

                      {/* Shortcut or chevron */}
                      {item.shortcut ? (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {item.shortcut.split(' ').map((k, i) => (
                            <kbd key={i} className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[10px] text-zinc-500 font-mono">
                              {k}
                            </kbd>
                          ))}
                        </div>
                      ) : (
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-zinc-400' : 'text-zinc-700'}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/5">
          <div className="flex items-center gap-3 text-[10px] text-zinc-700 font-mono">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-white/5 border border-white/10 rounded">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-white/5 border border-white/10 rounded">↵</kbd> select</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-white/5 border border-white/10 rounded">esc</kbd> close</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-zinc-700">
            <Command className="w-3 h-3" />
            <span>Signal Command</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
